import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetLocale, useTranslate } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Table, Input, Tag, Form, Row, Col, Button } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import type { ContentPublisherOut } from '@/api/generated/schemas';
import { Provider } from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import type { CrudFilter } from '@refinedev/core';
import { SelectFilter } from '@/components/common/SelectFilter';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';
import { PublisherCreateModal } from '@/components/content_catalog/publishers/PublisherCreateModal';
import { countryName } from '@/utils/countries';

export const ContentCatalogPublisherList = () => {
  const t = useTranslate();
  const locale = useGetLocale()() ?? 'pl';
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const rowNav = useTableRowNavigateToShow();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, sorters, setFilters, filters } =
    useAdminTable<ContentPublisherOut>({
      pagination: { pageSize: 20 },
      resource: 'content_catalog.publishers',
      onSearch,
      sorters: {
        initial: [{ field: 'id', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  return (
    <List
      canCreate={false}
      headerButtons={({ defaultButtons }) => (
        <>
          {defaultButtons}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
          >
            {t('content_catalog.publishers.create.button')}
          </Button>
        </>
      )}
    >
      <PublisherCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        // A new publisher's next step is setting up how it delivers.
        onCreated={(publisher) =>
          navigate(
            `/content_catalog/publishers/${publisher.id}?tab=integration`
          )
        }
      />
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align="middle">
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('content_catalog.publishers.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table
        {...rowNav('content_catalog.publishers', tableProps)}
        rowKey="id"
        pagination={{
          ...(typeof tableProps.pagination === 'object'
            ? tableProps.pagination
            : {}),
          showTotal: (total) => t('base.total', { total }),
        }}
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="name"
          title={t('content_catalog.publishers.fields.name')}
          sorter
          sortOrder={getSortOrder(sorters, 'name')}
        />

        <Table.Column
          dataIndex="provider"
          title={t('content_catalog.publishers.fields.provider')}
          render={(value: string) => (value ? <Tag>{value}</Tag> : '-')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'provider')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="provider"
              minWidth={160}
              placeholder={t('content_catalog.publishers.fields.provider')}
              options={Object.values(Provider).map((v) => ({
                label: v,
                value: v,
              }))}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="country_code"
          title={t('content_catalog.publishers.fields.country')}
          render={(value: string | null) =>
            value ? countryName(value, locale) : '-'
          }
        />

        <Table.Column
          dataIndex="has_active_contract"
          title={t('content_catalog.publishers.fields.has_active_contract')}
          sorter
          sortOrder={getSortOrder(sorters, 'has_active_contract')}
          filtered={isFieldFiltered(
            filters as CrudFilter[],
            'has_active_contract'
          )}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter<boolean>
              field="has_active_contract"
              minWidth={160}
              placeholder={t(
                'content_catalog.publishers.fields.has_active_contract'
              )}
              options={[
                {
                  label: t('content_catalog.publishers.contract.active'),
                  value: true,
                },
                {
                  label: t('content_catalog.publishers.contract.none'),
                  value: false,
                },
              ]}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: boolean) =>
            value ? (
              <Tag color="green">
                {t('content_catalog.publishers.contract.active')}
              </Tag>
            ) : (
              <Tag>{t('content_catalog.publishers.contract.none')}</Tag>
            )
          }
        />

        <Table.Column
          dataIndex="created_at"
          title={t('base.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'created_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="created_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />
      </Table>
    </List>
  );
};
